import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { ReactNode } from 'react';

type Identified = { id: string };

type SortableListProps<Item extends Identified> = {
  items: readonly Item[];
  label(item: Item): string;
  onChange(items: Item[]): void;
  render(item: Item, index: number): ReactNode;
  layout?: 'list' | 'grid';
  disabled?: boolean;
};

export function SortableList<Item extends Identified>({
  items,
  label,
  onChange,
  render,
  layout = 'list',
  disabled = false,
}: SortableListProps<Item>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const move = (from: number, to: number) => {
    if (disabled || to < 0 || to >= items.length) return;
    onChange(normalizePositions(arrayMove([...items], from, to)));
  };
  const dragEnd = (event: DragEndEvent) => {
    if (disabled || !event.over || event.active.id === event.over.id) return;
    const from = items.findIndex((item) => item.id === event.active.id);
    const to = items.findIndex((item) => item.id === event.over?.id);
    if (from >= 0 && to >= 0) move(from, to);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={dragEnd}>
      <SortableContext
        items={items.map((item) => item.id)}
        strategy={layout === 'grid' ? rectSortingStrategy : verticalListSortingStrategy}
      >
        <div className={layout === 'grid' ? 'sortable-list sortable-list--grid' : 'sortable-list'}>
          {items.map((item, index) => (
            <SortableRow
              key={item.id}
              id={item.id}
              label={label(item)}
              disabled={disabled}
              first={index === 0}
              last={index === items.length - 1}
              onUp={() => move(index, index - 1)}
              onDown={() => move(index, index + 1)}
            >
              {render(item, index)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  id,
  label,
  first,
  last,
  disabled,
  onUp,
  onDown,
  children,
}: {
  id: string;
  label: string;
  first: boolean;
  last: boolean;
  disabled: boolean;
  onUp(): void;
  onDown(): void;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      className="sortable-row"
      data-dragging={isDragging || undefined}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <div
        className="sortable-row__grip"
        {...attributes}
        {...listeners}
        tabIndex={disabled ? -1 : 0}
      >
        <span aria-hidden="true">⋮⋮</span>
        <span className="sr-only">Drag {label}</span>
      </div>
      <div className="sortable-row__body">{children}</div>
      <div className="sortable-row__moves">
        <button
          type="button"
          aria-label={`Move ${label} up`}
          disabled={disabled || first}
          onClick={onUp}
        >
          <ChevronUp size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label={`Move ${label} down`}
          disabled={disabled || last}
          onClick={onDown}
        >
          <ChevronDown size={14} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function normalizePositions<Item extends Identified>(items: Item[]): Item[] {
  return items.map((item, position) =>
    'position' in item ? ({ ...item, position } as Item) : item,
  );
}
